<script setup lang="ts">
import { VDateInput } from 'vuetify/labs/VDateInput'
import type MDBVegcoBucket from '@/types/vegco/bucket'
import type MDBVegcoCommandeVente from '@/types/vegco/commandevente'
import { notifyError, roundTo, emptyMongoId } from '@/utils/helpers'
import { unitésParBoîte, getDateCoupéForCulture } from '@/utils/helpers-vegco'

const storeU = useUtilisateurStore()
const storeVegco = useVegcoStore()
const dialog = ref(false)
const modifications = ref(false)

const choixProduit = ref('')
const produitsValides = ref<string[]>([])
const currentDate = ref(new Date())

const peuxAssigner = computed(() => !storeU.utilisateur.accèsNon.includes('site.VGCO.put.vgco.commandes.vente.assign'))

const bucketGauche = ref<MDBVegcoBucket>({} as MDBVegcoBucket)
const bucketDroite = ref<MDBVegcoBucket | null>(null as MDBVegcoBucket | null)
const bucketGaucheEstInventaire = computed(() => {
  return bucketGauche.value.semiId == emptyMongoId()
})
const bucketGaucheUtilisePrévision = computed(() => quantitéDroite.value > bucketGauche.value.quantité)

const commandeGauche = ref<MDBVegcoCommandeVente | null>(null as MDBVegcoCommandeVente | null)
const commandeDroite = ref<MDBVegcoCommandeVente | null>(null as MDBVegcoCommandeVente | null)

const splitQuantité = ref(0)
const quantitéGauche = ref(0)
const quantitéDroite = ref(0)
const quantitéDroiteUnits = ref(0)
const quantitéMaxDroite = ref(0)
const quantitéGauchePrévision = ref(undefined as number | undefined)

const publier = ref(false)
const commentairesDroite = ref('')
const noCommandeAchatDroite = ref('')
const unitParBoîte = computed(() => unitésParBoîte(bucketGauche.value.culture))
const unitParUnité = ref(1)
const unitParPalette = ref(1)
const parPalette = ref(false)
const codeProduitSlider = ref('')

const quantitéMaxGaucheToAssign = computed(() =>
  bucketGaucheEstInventaire.value ?
  bucketGauche.value.quantité + (quantitéGauchePrévision.value ?? 0) :
  bucketGauche.value.quantité
)

const quantitéGaucheAffichage = computed(() => {
  return roundTo(quantitéGauche.value / unitParBoîte.value, 2) + ' / ' +
  roundTo(quantitéMaxGaucheToAssign.value / unitParBoîte.value, 2)
})
const quantitéDroiteAffichage = computed(() => {
  if (commandeDroite.value != null && commandeDroite.value.statut == 'Inventaire') {
    return roundTo(quantitéDroite.value / unitParBoîte.value, 2)
  }

  return roundTo(quantitéDroite.value / unitParBoîte.value, 2) + ' / ' +
  roundTo(quantitéMaxDroite.value / unitParBoîte.value, 2)
})

const sliderMax = computed(() => {
  return Math.min(quantitéMaxGaucheToAssign.value + unitParBoîte.value, quantitéMaxDroite.value)
})

const unitParPaletteAffichage = computed({
  get: () => unitParPalette.value / unitParUnité.value,
  set: (v: number) => {
    if (!v || v < 1) {
      v = 1
    }
    unitParPalette.value = v * unitParUnité.value
    parPaletteChange()
  },
})

const peutModifierPalette = computed(() => commandeDroite.value?.statut == 'Inventaire')

let resolve: (value: boolean) => void
watch(dialog, (v) => {
  if (!v) {
    resolve(modifications.value)
  }
})

watch(splitQuantité, (_) => recalculerLesQuantités())
watch(choixProduit, (_) => onChoixProduitChange())
watch(parPalette, (_) => parPaletteChange())
watch(quantitéDroiteUnits, (_) => quantitéDroiteUnitsChanged())

function quantitéDroiteUnitsChanged() {
  let qté = quantitéDroiteUnits.value * unitParUnité.value
  if (qté < 0) {
    qté = 0
  }
  if (qté > quantitéMaxDroite.value) {
    qté = quantitéMaxDroite.value
  }
  splitQuantité.value = qté
  recalculerLesQuantités()
}

function parPaletteChange() {
  if (parPalette.value) {
    splitQuantité.value = Math.floor(splitQuantité.value / unitParPalette.value) * unitParPalette.value
  } else {
    splitQuantité.value = Math.floor(splitQuantité.value / unitParUnité.value) * unitParUnité.value
  }
  recalculerLesQuantités()
}

function recalculerLesQuantités() {
  quantitéGauche.value = quantitéMaxGaucheToAssign.value - splitQuantité.value
  quantitéDroite.value = splitQuantité.value
  if (quantitéGauche.value < 0) {
    quantitéGauche.value = 0
    quantitéDroite.value = quantitéMaxGaucheToAssign.value
  }

  quantitéDroiteUnits.value = quantitéDroite.value / unitParUnité.value
}

function calculateMaxToAssign(produit: string) {
  if (commandeDroite.value == null) {
    return quantitéMaxGaucheToAssign.value
  }

  const produitTrouvé = commandeDroite.value.détails.find((d) => d.codeProduit == produit)
  if (produitTrouvé == undefined) {
    notifyError('Impossible de trouver le produit dans la commande')
    dialog.value = false
    return 0
  }

  const total = storeVegco.bucketsStore.filter(x => x.commandeVenteId == commandeDroite.value?.id &&
    x.codeProduit == produit)
    .reduce((acc, y) => acc + y.quantité, 0)

  return produitTrouvé.quantité - total
}

function selectCodeProduitSlider(codeProduit: string) {
  const produit = storeU.getVegcoProduit(codeProduit)
  if (produit != undefined) {
    unitParUnité.value = produit.quantité
    codeProduitSlider.value = codeProduit
  }
  else {
    unitParUnité.value = unitParBoîte.value
    codeProduitSlider.value = ''
  }

  let détail = commandeDroite.value?.détails.find((d) => d.codeProduit == codeProduit)
  if (détail == undefined) {
    détail = commandeGauche.value?.détails.find((d) => d.codeProduit == codeProduit)
  }

  if (détail != undefined && détail.nombrePalette > 0) {
    unitParPalette.value = détail.quantité / détail.nombrePalette
    parPalette.value = true
  }
  else {
    unitParPalette.value = 1
    parPalette.value = false
  }
}

function onChoixProduitChange() {
  if (choixProduit.value == '') {
    return
  }

  bucketDroite.value = storeVegco.bucketsStore.find((b) =>
    b.commandeVenteId == commandeDroite.value?.id &&
    b.codeProduit == choixProduit.value &&
    (b.bucketParentId == bucketGauche.value.id ||
      b.bucketParentId == bucketGauche.value.bucketParentId)) || null

  selectCodeProduitSlider(choixProduit.value)
  quantitéMaxDroite.value = calculateMaxToAssign(choixProduit.value)

  if (commandeDroite.value != null) {
    splitQuantité.value = sliderMax.value
  } else {
    splitQuantité.value = sliderMax.value - bucketGauche.value.quantitéConfirmée
  }


  parPaletteChange()
  recalculerLesQuantités()
}

function openAssign(bucketLeft: MDBVegcoBucket, commandeVenteId: string, date: Date, quantitéPrévision: number | undefined) {
  modifications.value = false
  choixProduit.value = ''
  produitsValides.value = []
  currentDate.value = date
  publier.value = false
  splitQuantité.value = 0

  bucketGauche.value = { ... bucketLeft }
  quantitéGauche.value = quantitéMaxGaucheToAssign.value
  quantitéGauchePrévision.value = quantitéPrévision
  commandeGauche.value = bucketLeft.commandeVenteId == storeVegco.commandeInventaire?.id ?
    storeVegco.commandeInventaire :
    storeVegco.commandesStore.find((c) => c.id == bucketLeft.commandeVenteId) || null

  bucketDroite.value = null
  quantitéDroite.value = 0
  quantitéDroiteUnits.value = 0
  quantitéMaxDroite.value = 0
  commandeDroite.value = null
  commentairesDroite.value = ''
  noCommandeAchatDroite.value = ''

  unitParPalette.value = 1
  parPalette.value = false
  codeProduitSlider.value = ''

  unitParUnité.value = unitParBoîte.value

  if (commandeVenteId == '' || commandeVenteId == emptyMongoId()) {
    // Sans commande à sans commande
    if (bucketGauche.value.commandeVenteId == emptyMongoId()) {
      notifyError('Ne peut pas assigner un bucket à aucune commande')
      return false
    }

    if (bucketGauche.value.codeProduit != '') {
      selectCodeProduitSlider(bucketGauche.value.codeProduit)
    }

    // Dissocier un bucket
    quantitéMaxDroite.value = quantitéMaxGaucheToAssign.value
    splitQuantité.value = sliderMax.value - bucketGauche.value.quantitéConfirmée
    recalculerLesQuantités()
  }
  else {
    // D'une commande à une autre commande
    if (bucketGauche.value.commandeVenteId != emptyMongoId()) {
      notifyError("Ne peut pas assigner un bucket d'une commande à une autre commande")
      return false
    }

    let commandeFound = undefined as MDBVegcoCommandeVente | undefined
    if (storeVegco.commandeInventaire != null && commandeVenteId == storeVegco.commandeInventaire.id) {
      // Assignation à l'inventaire
      commandeFound = storeVegco.commandeInventaire

      if (bucketGaucheEstInventaire.value) {
        notifyError('Ne peut pas assigner un bucket d\'inventaire à la commande Pour Inventaire')
        return false
      }
    } else {
      // Assignation à une commande de vente
      commandeFound = storeVegco.commandesStore.find((c) => c.id == commandeVenteId)
      if (commandeFound == undefined) {
        notifyError('Impossible de trouver la commande')
        return false
      }

      // Seulement en cours
      if (commandeFound.statut != 'En cours') {
        notifyError(`Ne peut pas assigner un bucket à une commande ${commandeFound.statut}`)
        return false
      }

      // Set date to dateCoupé
      currentDate.value = getDateCoupéForCulture(commandeFound, bucketGauche.value.culture)
      currentDate.value.setHours(currentDate.value.getHours() - 6)
      // If the date is sunday, set to saturday
      if (currentDate.value.getDay() == 0) {
        currentDate.value.setDate(currentDate.value.getDate() - 1)
      }
      currentDate.value.setUTCHours(12)
    }

    commandeDroite.value = commandeFound

    // Trouver les produits valides
    for (const détail of commandeDroite.value.détails) {
      if (bucketGauche.value.codeProduit != '') {
        if (détail.codeProduit == bucketGauche.value.codeProduit) {
          produitsValides.value.push(détail.codeProduit)
        }
      }
      else if (détail.culture == bucketGauche.value.culture) {
        const produit = storeU.getVegcoProduit(détail.codeProduit)
        if (produit == undefined) {
          notifyError(`N'a pas pu trouvé le produit ${détail.codeProduit}`)
          return false
        }
        if (bucketGauche.value.balises.includes(produit.balise)) {
          produitsValides.value.push(détail.codeProduit)
        }
      }
    }

    // Trouve un produit valide à assigner
    for (const produit of produitsValides.value) {
      const max = calculateMaxToAssign(produit)
      if (max > 0) {
        choixProduit.value = produit
        onChoixProduitChange()
        break
      }
    }

    if (produitsValides.value.length == 0 || choixProduit.value == '') {
      notifyError('Aucun produit valide trouvé pour cette commande pour ce bucket')
      return false
    }
  }

  dialog.value = true
  return new Promise<boolean>((resolveFn) => {
    resolve = resolveFn
  })
}

async function confirm() {
  if (quantitéDroite.value == 0 && bucketGauche.value.quantité != 0) {
    notifyError('Aucune quantité assignée')
    dialog.value = false
    return
  }

  const unassign = commandeDroite.value == null || bucketGauche.value.commandeVenteId != emptyMongoId()
  const assign = commandeDroite.value != null && choixProduit.value != ''

  try
  {
    if (unassign) {
      const config = { params: { quantite: quantitéDroite.value } }
      await apiProphet.put(
        `vgco/commandes/vente/${bucketGauche.value.commandeVenteId}/dissocier/${bucketGauche.value.id}`,
        undefined, config)
      modifications.value = true
    }

    if (assign && commandeDroite.value != null) {
      currentDate.value.setUTCHours(12)
      const config = { params: {
        quantite: quantitéDroite.value,
        quantiteParPalette: unitParPalette.value,
        commentaires: commentairesDroite.value,
        noCommandeAchat: noCommandeAchatDroite.value,
        date: currentDate.value.toISOString(),
        prevision: bucketGaucheUtilisePrévision.value,
        publier: publier.value } }

      const bucketId = bucketGauche.value.bucketParentId == emptyMongoId() ?
        bucketGauche.value.id : bucketGauche.value.bucketParentId

      await apiProphet.put(
        `vgco/commandes/vente/${commandeDroite.value.id}/assigner/${bucketId}/${choixProduit.value}`,
        undefined, config)
      modifications.value = true
    }
  }
  catch (e) {
    notifyError(e)
  }

  dialog.value = false
}

function cancel() {
  dialog.value = false
}

defineExpose({ openAssign })
</script>

<template>
  <v-dialog v-model="dialog" max-width="900px" :fullscreen="$vuetify.display.smAndDown">
    <v-card style="z-index: -1">
      <v-card-title class="d-flex justify-space-between align-center">
        <span v-if="commandeDroite != null" class="text-h5 text-medium-emphasis ps-2">Assigner un bucket</span>
        <span v-else class="text-h5 text-medium-emphasis ps-2">Dissocier un bucket</span>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="cancel"/>
      </v-card-title>

      <v-divider class="mb-4"/>

      <v-card-text>

        <v-row>
          <v-col cols="12" md="3">
            <v-date-input
              v-if="commandeDroite != null" v-model="currentDate" label="Date"
              display-format="normalDateWithWeekday" prepend-icon="" class="me-2"/>
            <v-date-input
              v-else v-model="currentDate" label="Date"
              display-format="normalDateWithWeekday" prepend-icon="" class="me-2"
              readonly variant="outlined"/>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-if="commandeDroite != null" v-model="choixProduit" :items="produitsValides"
              label="Code de produit" class="me-2" outlined return-object prepend-icon="M2,6H4V18H2V6M5,6H6V18H5V6M7,6H10V18H7V6M11,6H12V18H11V6M14,6H16V18H14V6M17,6H20V18H17V6M21,6H22V18H21V6Z"/>
            <v-text-field
              v-else-if="bucketGauche.codeProduit != ''" v-model="bucketGauche.codeProduit" label="Code de produit"
              class="me-2" readonly variant="outlined" prepend-icon="M2,6H4V18H2V6M5,6H6V18H5V6M7,6H10V18H7V6M11,6H12V18H11V6M14,6H16V18H14V6M17,6H20V18H17V6M21,6H22V18H21V6Z"/>
          </v-col>
          <v-col cols="12" md="3">
            <v-checkbox
              v-if="unitParPalette > 1"
              v-model="parPalette" label="Par palette" class="me-2"/>
            <v-text-field
              v-if="unitParPalette > 1 && parPalette"
              v-model.number="unitParPaletteAffichage" type="number" min="1"
              :readonly="!peutModifierPalette" :variant="peutModifierPalette ? 'filled' : 'outlined'"
              label="Unités par palette" density="compact" class="me-2"/>
          </v-col>
        </v-row>
        <v-slider
          v-model="splitQuantité" :min="0" :max="sliderMax" :step="parPalette ? unitParPalette : unitParUnité"
          label="Quantité" thumb-label="always" class="mt-5">
          <template #thumb-label="{ modelValue }">
            {{ roundTo(modelValue / unitParUnité, 2) }}
          </template>
        </v-slider>
        <v-row class="mb-4">
          <v-col>
            <v-card>
              <v-card-title>
                <span v-if="bucketGaucheEstInventaire">Inventaire</span>
                <span v-else-if="commandeGauche == storeVegco.commandeInventaire">Commande Pour Inventaire</span>
                <span v-else-if="commandeGauche == null">Semi {{bucketGauche.numéroSemi}}</span>
                <span v-else>Commande No. {{commandeGauche.noCommande}}</span>
              </v-card-title>
              <v-card-text class="pt-3">
                <v-text-field v-model="quantitéGaucheAffichage" label="Quantitév bte42" readonly density="compact" variant="outlined"/>
                <v-text-field v-if="bucketGauche.codeProduit != ''" v-model="bucketGauche.codeProduit" label="Code de produit" readonly density="compact" variant="outlined"/>
                <v-textarea v-model="bucketGauche.commentaires" rows="2" label="Commentaires" readonly density="compact" variant="outlined"/>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="1" class="d-flex justify-center align-center">
            <v-icon icon="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z" size="40px" class="text-h5 text-medium-emphasis"/>
          </v-col>

          <v-col>
            <v-card>
              <v-card-title>
                <span v-if="commandeDroite == null && bucketGaucheEstInventaire">Retour à l'inventaire</span>
                <span v-else-if="commandeDroite == null">Semi {{bucketGauche.numéroSemi}}</span>
                <span v-else-if="commandeDroite.statut == 'Inventaire'">Pour inventaire</span>
                <span v-else>Commande No. {{commandeDroite.noCommande}}</span>
              </v-card-title>
              <v-card-text class="pt-3">
                <v-text-field
                  v-model="quantitéDroiteAffichage" label="Quantité bte42"
                  readonly density="compact" variant="outlined"/>
                <v-text-field
                  v-if="unitParUnité != 1" v-model="quantitéDroiteUnits"
                  type="number" min="0" :max="quantitéMaxDroite" label="Unités"/>
                <v-text-field
                  v-if="commandeDroite != null && bucketDroite == null" v-model="noCommandeAchatDroite"
                  label="No. commande achat" required/>
                <v-textarea
                  v-if="commandeDroite != null && bucketDroite == null" v-model="commentairesDroite"
                  rows="2" label="Commentaires" required/>
                <v-textarea
                  v-else-if="commandeDroite != null && bucketDroite != null" v-model="bucketDroite.commentaires"
                  rows="2" label="Commentaires" readonly density="compact" variant="outlined"/>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-select v-model="bucketGauche.balises" label="Balises" chips multiple readonly density="compact" variant="outlined"/>

        <v-row>
          <v-col>
            <v-checkbox
              v-if="commandeDroite != null && bucketDroite == null" v-model="publier" label="Publier immédiatement"
              class="mt-2" outlined prepend-icon="M5,4V6H19V4H5M5,14H9V20H15V14H19L12,7L5,14Z"/>
          </v-col>
          <v-col>
            <v-checkbox
              v-if="quantitéGauchePrévision != undefined" v-model="bucketGaucheUtilisePrévision"
              label="Va utiliser les prévisions" readonly
              class="mt-2" outlined prepend-icon="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.9L16.2,16.2Z"/>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider class="mt-2"/>

      <v-card-actions class="d-flex justify-end mr-2 my-2">
        <v-btn @click="cancel">Annuler</v-btn>
        <span v-if="peuxAssigner">
          <v-btn
            v-if="commandeDroite != null" color="primary" variant="flat"
            :disabled="splitQuantité == 0" @click="confirm">Assigner le bucket</v-btn>
          <v-btn
            v-else color="primary" variant="flat"
            :disabled="splitQuantité == 0 && bucketGauche.quantité != 0" @click="confirm">Dissocier le bucket</v-btn>
        </span>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
